import http from "http";
import open from "open";

import * as jose from "jose";
import {
  AuthenticationInfo,
  createClient,
  IAuthClient,
} from "@propelauth/javascript";

import { ConfigProvider } from "../config/provider.js";
import {
  loginFlowWithTimeout,
  TokensResponse,
  UserJwtClaims,
} from "./oauth.js";
import { logger } from "../logger.js";

import { AuthInfo } from "@modelcontextprotocol/sdk/server/auth/types.js";

/**
 * Extra authentication data for PropelAuthInfo
 */
export interface PropelAuthExtra extends Record<string, unknown> {
  userId?: string;
  email?: string;
  propelAccessToken?: string;
  propelRefreshToken?: string;
  apiKey?: string;
  redditAccessToken?: string;
  redditRefreshToken?: string;
}

/**
 * Extended authentication information with Propel-specific data.
 *
 * @remarks
 * This interface extends the base MCP AuthInfo type to include
 * Propel OAuth tokens and user information in the extra field.
 * The extra field is populated after successful OAuth authentication.
 *
 * @example
 * ```typescript
 * const authInfo: PropelAuthInfo = {
 *   clientId: "mcp-client-123",
 *   extra: {
 *     userId: "reddit_username",
 *     redditAccessToken: "access_token_here",
 *     redditRefreshToken: "refresh_token_here"
 *   }
 * };
 * ```
 */
export interface PropelAuthInfo extends AuthInfo {
  /**
   * Extra authentication data containing Propel-specific information.
   * Populated after successful OAuth flow completion.
   */
  extra?: PropelAuthExtra;
}

// Additional PropelAuth fields
export interface PropelAuthUserJwtClaims extends UserJwtClaims {
  user_id?: string;
  org_id_to_org_member_info?: Record<string, unknown>; // ! TODO: Get shape of this.
}

/** Decodes claims from a provided JWT or uses the access token from the config
 * if no token is provided.
 */
const getUserDetailsFromJwt = async (
  accessToken: string,
): Promise<PropelAuthUserJwtClaims> => {
  try {
    // Decode the JWT without verifying the signature
    const decoded = jose.decodeJwt(accessToken!);
    return decoded as PropelAuthUserJwtClaims;
  } catch (error) {
    logger.error(`Failed to decode JWT: ${error}`);
    throw new Error("Failed to extract user details from token");
  }
};

export class PropelAuthProvider {
  private readonly configProvider: ConfigProvider;

  constructor(params: { configProvider: ConfigProvider }) {
    this.configProvider = params.configProvider;
  }

  private async authClient(): Promise<IAuthClient> {
    const config = await this.configProvider.getConfig();
    const propelAuthUrl = config.app?.propelAuth?.url;
    if (!propelAuthUrl) {
      throw new Error("No PropelAuth URL found in config");
    }

    const authClient = createClient({
      // The base URL where your authentication pages are hosted.
      // You can find this under the Frontend Integration section for your project.
      authUrl: propelAuthUrl,

      // If true, periodically refresh the access token in the background.
      // This helps ensure you always have a valid token ready to go. Default true.
      enableBackgroundTokenRefresh: false,
    });

    return authClient;
  }

  async signupUrl(): Promise<URL> {
    const urlString = (await this.authClient()).getSignupPageUrl();
    return new URL(urlString);
  }

  async loginUrl(): Promise<URL> {
    const urlString = (await this.authClient()).getLoginPageUrl();
    return new URL(urlString);
  }

  async accountUrl(): Promise<URL> {
    const urlString = (await this.authClient()).getAccountPageUrl();
    return new URL(urlString);
  }

  async getAuthInfo(): Promise<AuthenticationInfo | null> {
    logger.debug("Getting auth info");
    const authClient = await this.authClient();
    return await authClient.getAuthenticationInfoOrNull();
  }

  async isLoggedIn(): Promise<boolean> {
    return (await this.getAuthInfo()) !== null;
  }

  async loginWithOAuthCallback(output: Console = console): Promise<{
    tokens: TokensResponse;
    userDetails: PropelAuthUserJwtClaims;
  }> {
    const config = await this.configProvider.getConfig();
    const oidcConfig = config.app?.oidc;
    if (!oidcConfig) {
      throw new Error("No OIDC config found in config");
    }
    const tokens = await loginFlowWithTimeout(oidcConfig, output);

    const userDetails = await getUserDetailsFromJwt(tokens.access_token);
    logger.debug(
      `PropelAuth claims extracted successfully: ${JSON.stringify(userDetails)}`,
    );
    return {
      tokens,
      userDetails,
    };
  }

  async logout(): Promise<void> {
    const authClient = await this.authClient();
    // TODO: Not sure if this is actually working.
    await authClient.logout(false);
  }
}
